// src/components/ModalGerenciarAssinatura.jsx
import React, { useEffect, useMemo, useState } from "react";
import { auth } from "../firebaseConfig";
import {
  FaCreditCard,
  FaSyncAlt,
  FaTimes,
  FaCheck,
  FaExclamationTriangle,
} from "react-icons/fa";

/* =================== Config =================== */
const BACKEND_URL =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_BACKEND_URL) ||
  process.env.REACT_APP_BACKEND_URL ||
  "https://api.gestorproapp.com.br";

const PERIODS = [
  { key: "mensal", label: "Mensal" },
  { key: "semestral", label: "Semestral" },
  { key: "anual", label: "Anual" },
];

const PLAN_LABEL = {
  box_start: "Box Start",
  box_turbo: "Box Turbo",
  box_master: "Box Master",
};

/* =================== Helpers =================== */
const digits = (s) => String(s || "").replace(/\D/g, "");

const fmtBRL = (vCents) =>
  (Number(vCents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const fmtMonth = (v) => {
  const n = digits(v).slice(0, 2);
  if (!n) return "";
  let mm = Number(n);
  if (!Number.isFinite(mm)) return "";
  if (mm < 1) mm = 1;
  if (mm > 12) mm = 12;
  return String(mm).padStart(2, "0");
};

const fmtYear = (v) => digits(v).slice(0, 4);

const formatCardNumber = (v) => {
  const d = digits(v).slice(0, 19);
  return d.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
};

function detectCardBrand(num) {
  const n = digits(num);
  if (/^4\d{0,18}$/.test(n)) return "Visa";
  if (/^(5[1-5]|2(2[2-9]|[3-6]\d|7[01]|720))/.test(n)) return "Mastercard";
  if (/^3[47]/.test(n)) return "Amex";
  if (
    /^(401178|401179|431274|438935|451416|457393|457631|457632|504175|627780|636297|636368|6500|6504|6505|6507|6509|6516|6550)/.test(
      n
    )
  )
    return "Elo";
  return null;
}

function toDateLabel(v) {
  if (!v) return "—";
  try {
    const d =
      typeof v === "string"
        ? new Date(v)
        : typeof v?.toDate === "function"
        ? v.toDate()
        : new Date(v);
    if (!Number.isFinite(d.getTime())) return "—";
    return d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
  } catch {
    return "—";
  }
}

async function safeJson(res) {
  const txt = await res.text();
  try {
    return JSON.parse(txt);
  } catch {
    return { raw: txt };
  }
}

async function authHeaderJSON() {
  const user = auth.currentUser;
  if (!user) throw new Error("Sessão expirada. Faça login novamente.");
  const token = await user.getIdToken(true);
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

/* =================== API =================== */
async function fetchMinhaAssinatura(empresaId) {
  const url = `${String(BACKEND_URL).replace(/\/+$/, "")}/billing/minha-assinatura?empresaId=${encodeURIComponent(
    empresaId
  )}`;
  const r = await fetch(url, { method: "GET", headers: await authHeaderJSON() });
  const js = await safeJson(r);

  if (r.status === 404) return null;
  if (!r.ok) throw new Error(js?.mensagem || js?.message || "Erro ao buscar assinatura.");
  return js;
}

async function tokenizarCartao(payload) {
  const url = `${String(BACKEND_URL).replace(/\/+$/, "")}/billing/tokenizar-cartao`;
  const r = await fetch(url, {
    method: "POST",
    headers: await authHeaderJSON(),
    body: JSON.stringify(payload),
  });
  const js = await safeJson(r);
  if (!r.ok) throw new Error(js?.error || js?.message || "Falha ao tokenizar o cartão.");
  if (!js?.tokenId) throw new Error(js?.error || "Token do cartão não retornou.");
  return js.tokenId;
}

async function atualizarCartaoAssinatura({ empresaId, tokenId, retryCharge }) {
  const url = `${String(BACKEND_URL).replace(/\/+$/, "")}/billing/atualizar-cartao-assinatura`;
  const r = await fetch(url, {
    method: "POST",
    headers: await authHeaderJSON(),
    body: JSON.stringify({ empresaId, tokenId, retryCharge: !!retryCharge }),
  });
  const js = await safeJson(r);
  if (!r.ok || !js?.ok) {
    throw new Error(js?.error || js?.message || "Falha ao atualizar cartão da assinatura.");
  }
  return js;
}

/* =================== Modal =================== */
export default function ModalGerenciarAssinatura({
  open,
  onClose,
  empresaId,
  currentPlanKey = "box_start",
  currentCycle = "mensal",
}) {
  const inputClass =
    "w-full p-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-gray-900 text-slate-900 dark:text-slate-100 outline-none focus:border-[#004072] focus:ring-2 focus:ring-blue-100 transition";

  const softCard =
    "rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4";

  const [tab, setTab] = useState("pagamento"); // 'pagamento' | 'assinatura'
  const [loading, setLoading] = useState(false);
  const [assinLoading, setAssinLoading] = useState(false);

  const [erro, setErro] = useState(null);
  const [okMsg, setOkMsg] = useState(null);

  // dados assinatura atual (do backend)
  const [assinatura, setAssinatura] = useState(null);

  // ---- Alterar pagamento (cartão)
  const [holder, setHolder] = useState("");
  const [number, setNumber] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [cvv, setCvv] = useState("");
  const [retryCharge, setRetryCharge] = useState(true);

  const brand = useMemo(() => detectCardBrand(number), [number]);

  // ---- Alterar assinatura (plano/ciclo)
  const [plans, setPlans] = useState([]); // [{key, name}]
  const [planKey, setPlanKey] = useState(currentPlanKey);
  const [period, setPeriod] = useState(currentCycle);
  const [installments, setInstallments] = useState(1);
  const [priceCents, setPriceCents] = useState(null);

  const isMensal = useMemo(() => String(period || "").toLowerCase() === "mensal", [period]);

  const maxInstallments = useMemo(
    () => (period === "mensal" ? 1 : period === "semestral" ? 6 : 12),
    [period]
  );

  const parcelas = useMemo(
    () => Array.from({ length: maxInstallments }, (_, i) => i + 1),
    [maxInstallments]
  );

  const loadAssinatura = async () => {
    if (!empresaId) return;
    setAssinLoading(true);
    try {
      const data = await fetchMinhaAssinatura(empresaId);
      setAssinatura(data);

      // se o backend retornar plan/cycle, sincroniza UI (sem sobrescrever se usuário já alterou manualmente)
      const bkPlan = data?.plan_key || data?.planKey;
      const bkCycle = data?.billing_cycle || data?.billingCycle;

      if (bkPlan && !plans.length) {
        // não depende de plans, só seta o valor
        setPlanKey(String(bkPlan));
      }
      if (bkCycle) setPeriod(String(bkCycle));
    } catch (e) {
      setAssinatura(null);
      setErro(e?.message || "Não foi possível carregar sua assinatura.");
    } finally {
      setAssinLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    setTab("pagamento");
    setErro(null);
    setOkMsg(null);
    setLoading(false);

    // reset form pagamento
    setHolder("");
    setNumber("");
    setExpMonth("");
    setExpYear("");
    setCvv("");
    setRetryCharge(true);

    // reset assinatura
    setPlanKey(currentPlanKey);
    setPeriod(currentCycle);
    setInstallments(1);
    setPriceCents(null);
    setAssinatura(null);

    // carrega assinatura atual e planos públicos
    (async () => {
      await loadAssinatura();

      try {
        const r = await fetch(`${String(BACKEND_URL).replace(/\/+$/, "")}/billing/planos-publico`);
        const js = await safeJson(r);
        if (r.ok && Array.isArray(js.items)) {
          setPlans(
            js.items.map((p) => ({
              key: p.key,
              name: PLAN_LABEL[p.key] || p.name || p.key,
            }))
          );
        } else {
          setPlans([
            { key: "box_start", name: "Box Start" },
            { key: "box_turbo", name: "Box Turbo" },
            { key: "box_master", name: "Box Master" },
          ]);
        }
      } catch {
        setPlans([
          { key: "box_start", name: "Box Start" },
          { key: "box_turbo", name: "Box Turbo" },
          { key: "box_master", name: "Box Master" },
        ]);
      }
    })();

  }, [open, empresaId, currentPlanKey, currentCycle]);

  // preview de preço (plan + period)
  useEffect(() => {
    if (!open) return;
    setPriceCents(null);

    (async () => {
      try {
        const url = `${String(BACKEND_URL).replace(/\/+$/, "")}/billing/price-preview?planKey=${encodeURIComponent(
          planKey
        )}&period=${encodeURIComponent(period)}`;
        const r = await fetch(url);
        const js = await safeJson(r);
        if (r.ok) setPriceCents(Number(js.amount_cents || 0));
      } catch {
        setPriceCents(null);
      }
    })();
  }, [open, planKey, period]);

  if (!open) return null;

  const closeOnBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  /* --------------- Ações --------------- */
  const handleAlterarPagamento = async () => {
    setErro(null);
    setOkMsg(null);

    if (!empresaId) {
      setErro("empresaId ausente.");
      return;
    }

    // Troca de cartão faz sentido principalmente para mensal (assinatura recorrente)
    if (!isMensal) {
      setErro(
        "Alteração de cartão é aplicável para assinatura mensal (recorrente). Para semestral/anual (pré-pago), o pagamento acontece via novo checkout/compra."
      );
      return;
    }

    const n = digits(number);
    const mm = Number(expMonth);
    const yyRaw = fmtYear(expYear);

    if (!holder?.trim() || !n || !expMonth || !yyRaw || !digits(cvv)) {
      setErro("Preencha todos os dados do cartão.");
      return;
    }
    if (n.length < 13 || n.length > 19) {
      setErro("Número do cartão inválido.");
      return;
    }
    if (!mm || mm < 1 || mm > 12) {
      setErro("Mês de validade inválido.");
      return;
    }

    // normaliza ano
    let yy = Number(yyRaw);
    if (!Number.isFinite(yy)) {
      setErro("Ano de validade inválido.");
      return;
    }
    if (yy < 100) yy = 2000 + yy;

    const cvvD = digits(cvv);
    if (cvvD.length < 3 || cvvD.length > 4) {
      setErro("CVV inválido.");
      return;
    }

    // valida expiração (fim do mês)
    try {
      const expEnd = new Date(yy, mm, 1);
      if (expEnd <= new Date()) {
        setErro("Este cartão parece estar vencido.");
        return;
      }
    } catch {
      // ignora
    }

    setLoading(true);
    try {
      // 1) tokenizar
      const tokenId = await tokenizarCartao({
        number: n,
        holder_name: holder.trim(),
        exp_month: mm,
        exp_year: yy,
        cvv: cvvD,
      });

      // 2) atualizar cartão da assinatura (+ tentativa de cobrança opcional)
      const res = await atualizarCartaoAssinatura({
        empresaId,
        tokenId,
        retryCharge,
      });

      setOkMsg(
        res?.retried
          ? "Cartão atualizado! Tentei cobrar novamente com o novo cartão."
          : "Cartão atualizado com sucesso para as próximas cobranças."
      );

      // limpa campos e recarrega resumo
      setHolder("");
      setNumber("");
      setExpMonth("");
      setExpYear("");
      setCvv("");

      await loadAssinatura();
    } catch (e) {
      setErro(e?.message || "Falha ao atualizar cartão.");
    } finally {
      setLoading(false);
    }
  };

  const handleAlterarAssinatura = async () => {
    setErro(null);
    setOkMsg(null);
    if (!empresaId) {
      setErro("empresaId ausente.");
      return;
    }

    setLoading(true);
    try {
      // 1) tenta rota dedicada (se existir no seu backend)
      const r = await fetch(`${String(BACKEND_URL).replace(/\/+$/, "")}/billing/alterar-assinatura`, {
        method: "POST",
        headers: await authHeaderJSON(),
        body: JSON.stringify({
          empresaId,
          planKey,
          billingCycle: period,
          installments: Math.max(1, Math.min(installments, maxInstallments)),
        }),
      });

      if (!r.ok) {
        // 2) fallback para fluxo padrão (criar/checkout)
        const f2 = await fetch(`${String(BACKEND_URL).replace(/\/+$/, "")}/billing/criar-assinatura`, {
          method: "POST",
          headers: await authHeaderJSON(),
          body: JSON.stringify({
            empresaId,
            uid: auth.currentUser?.uid || "",
            planKey,
            billingCycle: period,
            installments: Math.max(1, Math.min(installments, maxInstallments)),
            customer: {
              name: "Atualização de plano",
              email: auth.currentUser?.email || "cliente@exemplo.com",
              document: "00000000000",
            },
            card: { id: "default" }, // backend usa cartão salvo (mensal) / ou cria cobrança conforme sua regra
            trial_days: 0,
            metadata: { source: "portal:alteracao" },
          }),
        });

        if (!f2.ok) {
          const js = await safeJson(f2);
          throw new Error(js?.mensagem || js?.error || "Falha ao alterar a assinatura.");
        }
      }

      setOkMsg("Assinatura atualizada com sucesso!");
      await loadAssinatura();
    } catch (e) {
      setErro(e?.message || "Falha ao alterar assinatura.");
    } finally {
      setLoading(false);
    }
  };

  const resumoPlan = PLAN_LABEL[planKey] || planKey;
  const resumoPeriodo = PERIODS.find((p) => p.key === period)?.label || period;

  // resumo vindo do backend (se existir)
  const atualPlanKey = assinatura?.plan_key || assinatura?.planKey;
  const atualCycle = assinatura?.billing_cycle || assinatura?.billingCycle;
  const atualStatus = assinatura?.status || assinatura?.subscription_status || assinatura?.assinaturaStatus;
  const nextBillingAt = assinatura?.next_billing_at || assinatura?.nextBillingAt;
  const lastChargeStatus = assinatura?.last_charge_status || assinatura?.lastChargeStatus;
  const amountCentsBackend = assinatura?.amount_cents || assinatura?.amountCents;
  const cardLast4 = assinatura?.card_last4 || assinatura?.last4;
  const cardBrand = assinatura?.card_brand || assinatura?.brand;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4"
      onClick={closeOnBackdrop}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-3xl relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 pb-4 flex items-start justify-between gap-4 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h2 className="text-2xl font-extrabold text-[#004072] dark:text-white">
              Gerenciar assinatura
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Atualize pagamento e plano, e veja o status atual.
            </p>
          </div>

          <button
            onClick={onClose}
            className="text-gray-500 hover:text-[#004072] dark:text-gray-300 dark:hover:text-white"
            aria-label="Fechar"
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Resumo */}
        <div className="px-6 pt-5">
          <div className={softCard}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Assinatura atual</div>
                <div className="text-base font-bold text-gray-900 dark:text-white">
                  {assinLoading ? "Carregando…" : atualPlanKey ? (PLAN_LABEL[atualPlanKey] || atualPlanKey) : "—"}
                  {atualCycle ? (
                    <span className="ml-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                      • {PERIODS.find((p) => p.key === atualCycle)?.label || atualCycle}
                    </span>
                  ) : null}
                </div>
              </div>

              <button
                type="button"
                onClick={loadAssinatura}
                disabled={assinLoading}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border ${
                  assinLoading
                    ? "bg-gray-200 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700 cursor-not-allowed"
                    : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <FaSyncAlt /> Atualizar
              </button>
            </div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-3">
                <div className="text-gray-600 dark:text-gray-300">Status</div>
                <div className="font-bold text-gray-900 dark:text-white">
                  {assinLoading ? "…" : atualStatus || "—"}
                </div>
              </div>

              <div className="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-3">
                <div className="text-gray-600 dark:text-gray-300">Próxima cobrança</div>
                <div className="font-bold text-gray-900 dark:text-white">
                  {assinLoading ? "…" : toDateLabel(nextBillingAt)}
                </div>
              </div>

              <div className="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-3">
                <div className="text-gray-600 dark:text-gray-300">Última cobrança</div>
                <div className="font-bold text-gray-900 dark:text-white">
                  {assinLoading ? "…" : (lastChargeStatus || "—")}
                </div>
              </div>
            </div>

            {(amountCentsBackend != null || cardLast4) && (
              <div className="mt-3 text-sm text-gray-700 dark:text-gray-200">
                {amountCentsBackend != null ? (
                  <span className="mr-3">
                    <b>Valor:</b> {fmtBRL(amountCentsBackend)}
                  </span>
                ) : null}
                {cardLast4 ? (
                  <span>
                    <b>Cartão:</b> {cardBrand ? `${cardBrand} ` : ""}•••• {cardLast4}
                  </span>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 flex gap-2 flex-wrap">
          <button
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition ${
              tab === "pagamento"
                ? "bg-[#004072] text-white border-[#004072]"
                : "bg-gray-100 dark:bg-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
            }`}
            onClick={() => setTab("pagamento")}
          >
            Alterar forma de pagamento
          </button>

          <button
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition ${
              tab === "assinatura"
                ? "bg-[#004072] text-white border-[#004072]"
                : "bg-gray-100 dark:bg-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
            }`}
            onClick={() => setTab("assinatura")}
          >
            Alterar assinatura
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6 max-h-[70vh] overflow-y-auto space-y-6">
          {erro && (
            <div className="rounded-xl border border-rose-300 bg-rose-50 text-rose-700 p-3 text-sm">
              <FaExclamationTriangle className="inline mr-2" /> {erro}
            </div>
          )}
          {okMsg && (
            <div className="rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-700 p-3 text-sm">
              <FaCheck className="inline mr-2" /> {okMsg}
            </div>
          )}

          {tab === "pagamento" ? (
            <section className="space-y-4">
              {!isMensal && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-800 p-3 text-sm">
                  <FaExclamationTriangle className="inline mr-2" />
                  Sua seleção está em <b>{resumoPeriodo}</b>. Troca de cartão é mais útil no{" "}
                  <b>Mensal</b> (recorrente). Para semestral/anual (pré-pago), o pagamento é feito em uma nova compra.
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Titular do cartão">
                  <input className={inputClass} value={holder} onChange={(e) => setHolder(e.target.value)} />
                </Field>

                <Field label="Bandeira (detectada)">
                  <input className={inputClass} value={brand || "-"} disabled />
                </Field>

                <Field label="Número do cartão">
                  <input
                    className={`${inputClass} tracking-widest`}
                    placeholder="5555 5555 5555 4444"
                    value={formatCardNumber(number)}
                    onChange={(e) => setNumber(e.target.value)}
                    inputMode="numeric"
                    maxLength={23}
                  />
                </Field>

                <div className="grid grid-cols-3 gap-4">
                  <Field label="Mês">
                    <input
                      className={inputClass}
                      placeholder="MM"
                      value={expMonth}
                      onChange={(e) => setExpMonth(fmtMonth(e.target.value))}
                      inputMode="numeric"
                      maxLength={2}
                    />
                  </Field>

                  <Field label="Ano">
                    <input
                      className={inputClass}
                      placeholder="YYYY"
                      value={expYear}
                      onChange={(e) => setExpYear(fmtYear(e.target.value))}
                      inputMode="numeric"
                      maxLength={4}
                    />
                  </Field>

                  <Field label="CVV">
                    <input
                      className={inputClass}
                      placeholder="123"
                      value={cvv}
                      onChange={(e) => setCvv(digits(e.target.value).slice(0, 4))}
                      inputMode="numeric"
                      maxLength={4}
                    />
                  </Field>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 rounded-xl bg-blue-50 dark:bg-gray-800 p-3 text-sm text-blue-900 dark:text-gray-200 border border-blue-100 dark:border-gray-700">
                <div>
                  <FaCreditCard className="inline mr-2" />
                  O novo cartão será usado nas próximas cobranças (mensal).
                </div>

                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={retryCharge}
                    onChange={(e) => setRetryCharge(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <span>Tentar cobrar agora</span>
                </label>
              </div>
            </section>
          ) : (
            <section className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Plano">
                  <select className={inputClass} value={planKey} onChange={(e) => setPlanKey(e.target.value)}>
                    {(plans?.length ? plans : [
                      { key: "box_start", name: "Box Start" },
                      { key: "box_turbo", name: "Box Turbo" },
                      { key: "box_master", name: "Box Master" },
                    ]).map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Ciclo de cobrança">
                  <select
                    className={inputClass}
                    value={period}
                    onChange={(e) => {
                      setPeriod(e.target.value);
                      setInstallments(1);
                    }}
                  >
                    {PERIODS.map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label={`Parcelas ${period === "mensal" ? "(não aplicável)" : `(até ${maxInstallments}x)`}`}>
                  <select
                    className={inputClass}
                    value={installments}
                    onChange={(e) => setInstallments(Number(e.target.value))}
                    disabled={period === "mensal"}
                  >
                    {parcelas.map((n) => (
                      <option key={n} value={n}>
                        {n}x
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="rounded-xl bg-gray-50 dark:bg-gray-800 p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Valor {period !== "mensal" ? "(total do ciclo)" : "(mês)"}:
                  </span>
                  <strong className="text-lg text-gray-900 dark:text-white">
                    {priceCents != null ? fmtBRL(priceCents) : "—"}
                  </strong>
                </div>

                {period !== "mensal" && priceCents != null && (
                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    {installments}x de <b>{fmtBRL(Math.ceil(priceCents / Math.max(1, installments)))}</b> no cartão
                  </div>
                )}

                <div className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                  Selecionado: <b>{PLAN_LABEL[planKey] || planKey}</b> • <b>{resumoPeriodo}</b>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-5 bg-white/95 dark:bg-gray-900/95 border-t border-gray-200 dark:border-gray-800 flex gap-3">
          <button
            onClick={onClose}
            className="px-5 py-3 rounded-xl bg-gray-100 dark:bg-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            Fechar
          </button>

          {tab === "pagamento" ? (
            <button
              onClick={handleAlterarPagamento}
              disabled={loading}
              className={`ml-auto inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white font-semibold ${
                loading ? "bg-slate-400 cursor-not-allowed" : "bg-[#004072] hover:brightness-110"
              }`}
            >
              <FaSyncAlt /> {loading ? "Processando…" : "Salvar novo cartão"}
            </button>
          ) : (
            <button
              onClick={handleAlterarAssinatura}
              disabled={loading}
              className={`ml-auto inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white font-semibold ${
                loading ? "bg-slate-400 cursor-not-allowed" : "bg-[#004072] hover:brightness-110"
              }`}
            >
              <FaSyncAlt /> {loading ? "Processando…" : "Aplicar alteração"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============ Subcomponent ============ */
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold mb-1 dark:text-gray-200">{label}</label>
      {children}
    </div>
  );
}
